import { D, money, sum } from './money';
import type { Asset, BankState, Derivative, ObsContract, Signal } from './types';

export const quarterlyPayment = (principal: ReturnType<typeof D>, annualRate: ReturnType<typeof D>, quarters: number) => {
  const i = annualRate.div(4);
  return money(principal.times(i.times(D(1).plus(i).pow(quarters))).div(D(1).plus(i).pow(quarters).minus(1)));
};
export const outstandingAfter = (principal: ReturnType<typeof D>, annualRate: ReturnType<typeof D>, quarters: number, elapsed: number) => {
  const i = annualRate.div(4), one = D(1).plus(i);
  return money(principal.times(one.pow(quarters).minus(one.pow(elapsed))).div(one.pow(quarters).minus(1)));
};
export const assetCarrying = (a: Asset) => a.assetClass === 'DISTRESSED_PACK' ? (a.amortisedCost ?? D(0)) : a.outstanding;
export const totalAssets = (s: BankState) => money(s.cash.plus(sum(s.assets.map(assetCarrying))));
export const totalLiabilities = (s: BankState) => money(sum(s.liabilities.map(l => l.balance)));
export const cet1 = (s: BankState) => money(s.paidInCapital.plus(s.retainedEarnings));
export const tier2 = (s: BankState) => money(DecimalMin(sum(s.liabilities.filter(l => l.kind === 'SUBORDINATED').map(l => l.balance)), cet1(s)));
const DecimalMin = (a: ReturnType<typeof D>, b: ReturnType<typeof D>) => a.lessThan(b) ? a : b;
export const obsRwa = (o: ObsContract) => o.status === 'ACTIVE' ? o.notional.times(o.ccf).times(o.counterpartyWeight) : D(0);
export const derivativeRwa = (d: Derivative) => {
  const addOn = d.kind === 'IR_SWAP' ? (d.tenorYears <= 1 ? 0 : d.tenorYears <= 5 ? .005 : .015) : (d.tenorYears <= 1 ? .01 : d.tenorYears <= 5 ? .05 : .075);
  return DecimalMax(d.cumulativeMtm, D(0)).plus(d.notional.times(addOn)).times(d.counterpartyWeight);
};
const DecimalMax = (a: ReturnType<typeof D>, b: ReturnType<typeof D>) => a.greaterThan(b) ? a : b;
export const rwa = (s: BankState) => money(sum([
  ...s.assets.map(a => assetCarrying(a).times(a.status === 'DEFAULTED' ? 1 : a.riskWeight)),
  ...s.offBalance.map(obsRwa), ...s.derivatives.map(derivativeRwa)
]));
export const ratios = (s: BankState) => {
  const r = rwa(s), core = cet1(s), capital = core.plus(tier2(s));
  const car = r.isZero() ? D(9.99) : capital.div(r), tier1Ratio = r.isZero() ? D(9.99) : core.div(r);
  return { rwa: r, cet1: core, tier2: tier2(s), totalCapital: capital, car, tier1Ratio };
};
export const determineSignal = (s: BankState): Signal => {
  const m = ratios(s);
  if (s.cash.isNegative() || m.cet1.isNegative() || m.car.lt(.04) || m.tier1Ratio.lt(.02)) return 'RED';
  const nextMaturing = sum(s.liabilities.filter(l => l.maturityQuarter === s.quarter + 1).map(l => l.balance));
  const hqla = s.cash.plus(sum(s.assets.filter(a => a.assetClass === 'SOVEREIGN' && a.maturityQuarter - s.quarter <= 4).map(assetCarrying)));
  return m.car.lt(.105) || m.tier1Ratio.lt(.06) || hqla.lt(nextMaturing) ? 'YELLOW' : 'GREEN';
};
